'use client';

import { useContext, useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { TasksController } from '@nx-frontend/features/tasks-calendar';
import { NoSSREditorReact } from '@nx-frontend/features/web-editor';
import { TasksSocketService, TaskEventsService } from '@nx-frontend/features/web-editor/plugins';
import { CommentService } from '@nx-frontend/features/web-editor/plugins/commentManager';
import { Head } from '@nx-frontend/shared/ui';
import { getContextMenuPositionByTarget } from '@nx-frontend/shared/ui/context-menu';
import { openModalEvent, closeModalEvent } from '@nx-frontend/shared/ui/modal';
import { SkeletonType, SkeletonWrapper } from '@nx-frontend/shared/ui/skeletons';
import { useToast } from '@nx-frontend/shared/ui/toast';
import {
  showAiLimitPopup,
  showAttachmentSizeLimitPopup,
  showStorageSizeLimitPopup,
  showTrafficLimitPopup,
  showBlotsLimitPopup,
} from '@nx-frontend/shared/actions';
import { USER_VARIABLE } from '@nx-frontend/shared/api';
import { getIsAddonWasPurshasedEarly } from '@nx-frontend/shared/api/webnotes/get-is-addon-was-purchased-early';
import { generalEnv } from '@nx-frontend/shared/env';
import { useBuyGenAiAddon, useGetNoteInMenu } from '@nx-frontend/shared/hooks';
import { useRouter, useParams, useSearchParams } from '@nx-frontend/shared/hooks/router-proxy';
import { useCreateNote } from '@nx-frontend/shared/hooks/use-create/use-note-create';
import { SocketContext } from '@nx-frontend/shared/providers';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { setAttachmentFullscreenEvent } from '@nx-frontend/shared/store/editor-attachment';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { showImageViewerForPageEvent } from '@nx-frontend/shared/store/image-viewer/page-image-viewer';
import { $note, setNoteEvent, $isNoteFetching } from '@nx-frontend/shared/store/note';
import {
  $notes,
  changeNoteVisibilityEvent,
  changeNoteVisibilityInPortalFx,
  updateNoteEvent,
} from '@nx-frontend/shared/store/notes';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $orgLimits } from '@nx-frontend/shared/store/org-limits';
import { $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $isOwner } from '@nx-frontend/shared/store/org-role';
import {
  $orgUsageSummary,
  OrgUsageSummaryStore,
  incStorageSizeEvent,
  decStorageSizeEvent,
  getStorageUsageFx,
} from '@nx-frontend/shared/store/org-usage-summary';
import { $portalSettings } from '@nx-frontend/shared/store/portal';
import {
  $transcriptPanel,
  showTranscriptPanelEvent,
  hideTranscriptPanelEvent,
  setActiveTabToAskAIEvent,
  updateAIChatEvent,
} from '@nx-frontend/shared/store/transcript-panel';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $user } from '@nx-frontend/shared/store/user';
import { $dateTimeLocale, setUserVarEvent, $userVars } from '@nx-frontend/shared/store/user-vars';
import { $webEditor, setWebEditorEvent } from '@nx-frontend/shared/store/web-editor-store';
import { $workspaceAccess, $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import {
  AppLimitError,
  EditorCreateNoteParams,
  EditorInstance,
  EditorPageUsersAccess,
  Org,
  OrgInfoSubscriptionType,
  OrganizationPrivilege,
  OrgMemberRole,
  PaywallPopupReason,
  PopupType,
  TasksListType,
  UserVar,
  WorkspaceRole,
  AiUsage,
  NotesStoreNote,
  EditorColorScheme,
} from '@nx-frontend/shared/types';
import { WorkspacePrivilege } from '@nx-frontend/shared/types';
import { ModalTitleSize, OpenModalFn } from '@nx-frontend/shared/types';
import { GetOpenPayWallPopupProps, GetOpenUnlockAIPopupProps } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { positionTypeToMenuType } from '@nx-frontend/shared/utils';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';
import { webnotesClientEnv } from '@nx-frontend/webnotes-client/shared';
import { activateGuideGenEvent } from '@nx-frontend/webnotes-client/shared/store';
import { PageNotFound, PortalPageMakeInvisible } from '@nx-frontend/webnotes-client/shared/ui';
import { Guidegen, NoteTitle, NoteTopbar } from '@nx-frontend/webnotes-client/widgets';
import { getOpenInvitePopup } from '@nx-frontend/widgets/popups';
import { getOpenAddonPopup } from '@nx-frontend/widgets/popups';
import { Topbar, TopbarContext } from '@nx-frontend/widgets/topbar';
import { TranscriptPanel, TranscriptPanelHideBtn } from '@nx-frontend/widgets/transcript-panel';
import { NoteTags } from './note-tags-list';

const TRIGGERER_MAKE_NOTE_INVISIBLE_LINE = 'make-note-invisible-line';

function getBlotsLimit(
  orgUsageSummary: OrgUsageSummaryStore,
  openModal: OpenModalFn,
  subscriptionType: OrgInfoSubscriptionType,
  currentOrganization: Org,
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void
) {
  if (
    !orgUsageSummary.isLoaded ||
    typeof orgUsageSummary.data?.maxBlots !== 'number' ||
    typeof orgUsageSummary.data?.currentBlots !== 'number'
  ) {
    return {
      enabled: false,
    };
  }
  if (subscriptionType !== OrgInfoSubscriptionType.STANDARD) {
    // for non-standard plans blots limit is not applied
    // FIXME should be done on org privileges level without direct checking the subscription type
    return {
      enabled: false,
    };
  }
  const res = {
    enabled: true,
    limit: orgUsageSummary.data?.maxBlots - orgUsageSummary.data?.currentBlots,
    alert: () => {
      showBlotsLimitPopup({
        openModal,
        subscriptionType,
        currentOrganization,
        getOpenPaywallPopup,
      });
    },
  };
  return res;
}

const onRateEditorClick = () => {
  if (window.userpilot) {
    window.userpilot.trigger('survey:ZLgSjzQ7uv');
  } else {
    console.warn('userpilot is not avaialble to trigger editor survey');
  }
};

interface NoteProps {
  getOpenPaywallPopup: (props: GetOpenPayWallPopupProps) => void;
  getOpenUnlockAIPopup: (props: GetOpenUnlockAIPopupProps) => void;
}

export function Note({ getOpenPaywallPopup, getOpenUnlockAIPopup }: NoteProps) {
  const [
    app,
    user,
    currentWorkspace,
    role,
    orgInfo,
    billingInfo,
    currentNote,
    notes,
    dateTimeLocale,
    workspaceAccess,
    webEditor,
    setWebEditor,
    canManageMembers,
    isOwner,
    setCurrentNote,
    menuState,
    portalSettings,
    isCurrentNoteFetching,
    orgUsageSummary,
    currentOrganization,
    unleashFlags,
    showImageViewerForPage,
    orgLimits,
    transcriptPanel,
    orgCoupons,
    setUserVar,
    userVars,
  ] = useUnit([
    $app,
    $user,
    $currentWorkspace,
    $orgRoleState,
    $orgInfo,
    $billingInfo,
    $note,
    $notes,
    $dateTimeLocale,
    $workspaceAccess,
    $webEditor,
    setWebEditorEvent,
    $wpCanManageMembers,
    $isOwner,
    setNoteEvent,
    $menuState,
    $portalSettings,
    $isNoteFetching,
    $orgUsageSummary,
    $currentOrganization,
    $unleashFlags,
    showImageViewerForPageEvent,
    $orgLimits,
    $transcriptPanel,
    $orgCoupons,
    setUserVarEvent,
    $userVars,
  ]);
  const [
    openModal,
    closeModal,
    showTranscriptPanel,
    hideTranscriptPanel,
    setActiveTabToAskAI,
    updateAIChat,
    activateGuideGen,
    incStorageSize,
    decStorageSize,
    getStorageUsage,
    setAttachmentFullscreen,
  ] = useUnit([
    openModalEvent,
    closeModalEvent,
    showTranscriptPanelEvent,
    hideTranscriptPanelEvent,
    setActiveTabToAskAIEvent,
    updateAIChatEvent,
    activateGuideGenEvent,
    incStorageSizeEvent,
    decStorageSizeEvent,
    getStorageUsageFx,
    setAttachmentFullscreenEvent,
  ]);

  const { toast } = useToast();
  const createNote = useCreateNote();
  const editorRef = useRef<EditorInstance | null>(null);
  const userVarsRef = useRef<UserVar[]>([]);
  userVarsRef.current = userVars;

  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const slug = useMemo(() => (params?.slug as string[]) || [], [params]);
  const socket = useContext(SocketContext);
  const isExtendedManageEnabled = workspaceAccess.role === 'admin';
  const canEdit = [WorkspaceRole.EDITOR, WorkspaceRole.ADMIN].includes(workspaceAccess.role);
  const canBlotLocking =
    orgInfo.subscriptionPrivileges.includes(OrganizationPrivilege.CAN_ACCESS_BLOT_LOCKING) &&
    (workspaceAccess.privileges?.includes(WorkspacePrivilege.CAN_ACCESS_BLOT_LOCKING) ||
      role.privileges.includes(OrganizationPrivilege.CAN_ACCESS_BLOT_LOCKING));

  const t = useTranslations();
  const tFeatures = useTranslations('features');

  const [, , pathNoteId] = slug;
  const mode = canEdit && currentNote.editnote ? 'edit' : 'readonly';

  const entities = notes.workspaces[currentWorkspace.globalId]?.entities || {};
  const note: NotesStoreNote | undefined = entities[currentNote.globalId];

  const openGenAiAddonPopup = useBuyGenAiAddon({
    getOpenPaywallPopup,
    getOpenAddonPopup,
    getIsAddonWasPurshasedEarly,
  });

  const routerType = NextRouterType.APP;
  const routerPushFn = router.push;

  useEffect(() => {
    if (pathNoteId !== currentNote.globalId) {
      setWebEditor(null);
      setCurrentNote({ workspaceId: currentWorkspace.globalId, noteId: pathNoteId });
      document.querySelector('#main-editor-scrolling-container')?.scrollTo(0, 0);
    }
  }, [currentNote.globalId, currentWorkspace.globalId, pathNoteId, setWebEditor, setCurrentNote]);

  useEffect(() => {
    // init ones tasks socket events when socket is connected
    if (socket && currentNote && pathNoteId === currentNote.globalId) {
      TasksSocketService.init(socket);
      CommentService.init(socket, {
        sessionId: app.sessionId,
        workspaceId: currentNote.workspaceId,
        noteId: currentNote.globalId,
      });
    }
  }, [socket, app.sessionId, pathNoteId, currentNote]);

  const goToTasksDashboard = useCallback(() => {
    router.push('/space/' + currentWorkspace.globalId + '/tasks');
  }, [currentWorkspace.globalId, router]);

  const showTranscriptAd = useCallback(
    (attachGlobalId: string, webEditor: EditorInstance) => {
      setUserVar({
        key: USER_VARIABLE.TRANSCRIPT_AD_SHOWN,
        value: true,
      });
      openModal({
        content: PopupType.TRANSCRIPT_AD,
        showCloseButton: false,
        usePadding: false,
        multipopup: false,
        payload: {
          payloadType: PopupType.TRANSCRIPT_AD,
          onClose: () => {
            closeModal();
          },
          onTranscriptRequest: () => {
            const blots = webEditor.plugins.attachmentManager?.findBlotsWithAttachment(attachGlobalId);
            if (blots && blots.length) {
              const blot = blots[0].blot;
              if (blot) {
                const attachment = Object.assign({}, blot.props.attachment) as {
                  url: string;
                  displayName: string;
                };
                const video = document.createElement('video');
                video.src = attachment.url;
                video.onloadedmetadata = function () {
                  showTranscriptPanel({
                    globalId: attachGlobalId,
                    url: attachment.url,
                    displayName: attachment.displayName,
                    duration: video.duration,
                  });
                };
              }
            }
            closeModal();
          },
        },
      });
    },
    [openModal, closeModal, showTranscriptPanel, setUserVar]
  );

  const onAttachmentCreated = useCallback(
    async (attachData: { globalId: string; mime: string }) => {
      // use refs here instead of store values because editor does not react to its props updates

      if (role.role !== OrgMemberRole.OWNER) {
        return;
      }

      const userVarValue = userVarsRef.current.find((v) => v.key === USER_VARIABLE.TRANSCRIPT_AD_SHOWN);
      if (userVarValue && userVarValue.value) {
        return;
      }
      if (attachData.mime && attachData.mime.startsWith('video/')) {
        if (editorRef.current) {
          showTranscriptAd(attachData.globalId, editorRef.current);
        }
      }
    },
    [showTranscriptAd, role]
  );

  useEffect(() => {
    TaskEventsService.openInvitePopup = getOpenInvitePopup(
      { role, orgInfo, billingInfo, currentWorkspace, canManageMembers },
      openModal,
      { routerType, routerPushFn }
    );

    TaskEventsService.navigateToDashboard = () => {
      goToTasksDashboard();
    };

    TaskEventsService.init(toast, t);
  }, [
    toast,
    t,
    role,
    orgInfo,
    billingInfo,
    currentWorkspace,
    canManageMembers,
    openModal,
    routerType,
    routerPushFn,
    goToTasksDashboard,
  ]);

  useEffect(() => {
    // init tasks calendar controller
    TasksController.getInstance().init(app.sessionId, generalEnv.gatewayUrl, socket, tFeatures);
  }, [app.sessionId, tFeatures, socket]);

  useEffect(() => {
    return () => {
      hideTranscriptPanel();
    };
  }, [hideTranscriptPanel]);

  const noteMenuItem = useGetNoteInMenu();

  const onGetTextFromImage = useCallback(
    (attachmentId: string) => {
      const subscriptionType = orgInfo.subscriptionType;
      if (subscriptionType !== 'standard' || !isOwner) {
        openModal({
          title: 'Text from image',
          content: PopupType.TEXT_FROM_IMAGE,
          closeButtonSize: 18,
          titleSize: ModalTitleSize.MD,
          payload: {
            payloadType: PopupType.TEXT_FROM_IMAGE,
            isFeatureAvailable: subscriptionType !== 'standard',
            workspaceId: currentWorkspace.globalId,
            attachmentId,
          },
        });
      } else {
        getOpenPaywallPopup({
          openModal,
          payload: {
            payloadType: PopupType.PAYWALL,
            title: 'Upgrade your plan to search your info in images and files!',
            reason: PaywallPopupReason.OCR,
          },
        });
      }
    },
    [currentWorkspace.globalId, openModal, orgInfo, isOwner, getOpenPaywallPopup]
  );

  const onSetHintIcon = useCallback(
    (blotId: string, target: HTMLElement): Promise<Record<string, unknown>> => {
      return new Promise((resolve) => {
        let targetNode: HTMLElement | null = null;
        if (!target) {
          const editor = webEditor || window.gEditor;
          if (editor) {
            const blot = editor.findById(blotId);
            if (blot) {
              targetNode = blot.domNode;
            }
          }
        } else {
          targetNode = target;
        }
        if (targetNode !== null) {
          openModal({
            content: PopupType.EMOJI,
            showCloseButton: false,
            payload: {
              payloadType: PopupType.EMOJI,
              allowRemove: false,
              onApply: (value: string) => {
                resolve({ icon: value });
              },
            },
            position: getContextMenuPositionByTarget(targetNode),
            additionalLayoutStyles: 'bg-transparent',
          });
        } else {
          resolve({});
        }
      });
    },
    [openModal, webEditor]
  );

  const onMentionClick = useCallback(
    (type: string, globalId: string, workspaceIdArg: string) => {
      const workspaceId = !workspaceIdArg ? currentWorkspace.globalId : workspaceIdArg;
      if (!type || !globalId || !workspaceId) {
        return toast({ title: 'Failed to navigate to mention' });
      }
      switch (type) {
        case 'folder':
          router.push('/space/' + workspaceId + '/folder/' + globalId);
          break;
        case 'note':
          router.push('/space/' + workspaceId + '/page/' + globalId);
          break;
        case 'workspace':
          router.push('/space/' + globalId + '/all');
          break;
      }
    },
    [toast, router, currentWorkspace]
  );

  const onTaskListRender = useCallback(
    (node: Element, taskListId: string, type: TasksListType, options = {}) => {
      TasksController.getInstance().render(taskListId, type, currentWorkspace.globalId, node, {
        calendar: { height: 'auto' },
        showCreateTaskButtons: true,
        editable: mode === 'edit',
        isPortal: !!searchParams?.get('portal-page-url'),
        ...options,
      });
    },
    [currentWorkspace.globalId, mode, searchParams]
  );

  const onTasksFeatureComponentPropsChanged = useCallback((featureId: string, props: Record<string, unknown>) => {
    TasksController.getInstance().onFeaturePropsChanged(featureId, props);
  }, []);

  const [changeNoteVisibility, updateNote] = useUnit([changeNoteVisibilityEvent, updateNoteEvent]);
  const [invislibleSlideUp, setInvisilbeSlideUp] = useState<boolean>(false);

  const handleMakeInvisibleClick = useCallback(async () => {
    changeNoteVisibility({
      workspaceId: currentWorkspace.globalId,
      globalId: currentNote.globalId,
      visible: false,
      triggerer: TRIGGERER_MAKE_NOTE_INVISIBLE_LINE,
    });
  }, [changeNoteVisibility, currentWorkspace.globalId, currentNote.globalId]);

  useEffect(() => {
    const sub = changeNoteVisibilityInPortalFx.doneData.watch(({ triggerer }) => {
      if (triggerer !== TRIGGERER_MAKE_NOTE_INVISIBLE_LINE) {
        return;
      }
      setInvisilbeSlideUp(true);
      toast({ title: 'Page is invisible in portal' });
      setTimeout(() => {
        setInvisilbeSlideUp(false);
        updateNote({
          workspaceId: currentWorkspace.globalId,
          globalId: currentNote.globalId,
          showMakeInvisibleNotification: false,
        });
      }, 200);
    });
    return () => {
      sub.unsubscribe();
      updateNote({
        workspaceId: currentWorkspace.globalId,
        globalId: currentNote.globalId,
        showMakeInvisibleNotification: false,
      });
    };
  }, [setInvisilbeSlideUp, toast, updateNote, currentWorkspace.globalId, currentNote.globalId]);

  if (currentNote && !currentNote.globalId && !isCurrentNoteFetching) {
    return <PageNotFound />;
  }

  const parentFolder = notes.workspaces[currentWorkspace.globalId]?.entities[currentNote.parentId];
  let pageTitle = `${currentNote?.title} - ${currentWorkspace.title}`;

  if (parentFolder?.title) {
    pageTitle = `${currentNote?.title} - ${parentFolder.title} - ${currentWorkspace.title}`;
  }

  let usersAccess: EditorPageUsersAccess | null = null;
  if (currentNote.isSharedForPortal) {
    const portalMenuItem = notes.workspaces[currentWorkspace.globalId]?.entities[currentNote.globalId]?.portalMenuItem;
    if (portalMenuItem && portalMenuItem.permissions) {
      const { permissions } = portalMenuItem;
      if (permissions.rejectAll) {
        usersAccess = {};
      } else if (permissions.allowAll) {
        usersAccess = null;
      } else {
        usersAccess = permissions.allowUserIds.reduce((acc: Record<number, boolean>, userId) => {
          acc[userId] = true;
          return acc;
        }, {});
      }
    }
    if (portalMenuItem && portalMenuItem.positionType) {
      const menuType = positionTypeToMenuType(portalMenuItem.positionType);
      const parentItem = menuState[menuType].entities[portalMenuItem.parentId];
      if (parentItem) {
        pageTitle = `${currentNote?.title} - ${parentItem.name} - ${portalSettings.name}`;
      } else {
        pageTitle = `${currentNote?.title} - ${portalSettings.name}`;
      }
    }
  }

  return (
    <div className="flex flex-col w-full h-full relative">
      <Head>
        <title>{pageTitle}</title>
      </Head>
      {currentNote && (
        <TopbarContext.Provider value={{ noteMenuItem }}>
          <Topbar cy="page-topbar" bradcrumbsWrapperClassName="max-w-[540px] mr-auto">
            <NoteTopbar
              getOpenPaywallPopup={getOpenPaywallPopup}
              globalId={currentNote.globalId}
              isCurrentNoteFetching={isCurrentNoteFetching}
            />
          </Topbar>
          {noteMenuItem?.visible && note?.showMakeInvisibleNotification && (
            <PortalPageMakeInvisible
              onClick={handleMakeInvisibleClick}
              showLoader={!!note?.isMakingInvisible}
              slideUp={!!invislibleSlideUp}
            />
          )}
        </TopbarContext.Provider>
      )}
      <div
        className={classNames({ 'is-note-fullwidth': currentNote.isFullwidth }, 'ml-[3px] overflow-y-auto grow flex')}
        id="main-editor-scrolling-container"
      >
        <div className="grow pt-40 w-full">
          <NoteTitle />
          {unleashFlags[UnleashFeatures.NOTE_TAGS] && (
            <div className={classNames('mt-3 pr-10 pl-[30px]')}>
              <div
                className={classNames(
                  {
                    'max-w-1000': !currentNote.isFullwidth,
                  },
                  'mx-auto'
                )}
              >
                <NoteTags />
              </div>
            </div>
          )}
          {!webEditor && (
            <SkeletonWrapper
              type={SkeletonType.WEBNOTES_NOTE}
              isFullWidth={currentNote.isFullwidth}
              editorColorScheme={EditorColorScheme.LIGHT}
            />
          )}
          <NoSSREditorReact
            t={t}
            options={{
              mode,
              note: currentNote,
              workspaceId: currentWorkspace.globalId,
              orgId: orgInfo.orgId,
              ...(currentNote.dump ? { dump: currentNote.dump } : {}),
              language: app.language,
              HTMLPublicKey: generalEnv.htmlPublicKey,
              gatewayUrl: generalEnv.gatewayUrl,
              sessionId: app.sessionId,
              user: user,
              userRole: role.role,
              isWorkspaceMember: true,
              usersAccess,
              portalSettingsWorkspace: currentWorkspace.globalId,
              staticAssetPrefix: webnotesClientEnv.staticAssetPrefix,
              dumpServerUrl: generalEnv.dumpServerUrl,
              tasksBundleJsUrl: generalEnv.tasksBundleJsUrl,
              tasksBundleCssUrl: generalEnv.tasksBundleCssUrl,
              commentsBundleJsUrl: generalEnv.commentsBundleJsUrl,
              aiServiceUrl: generalEnv.aiServiceUrl || '',
              markdownEnabled: true,
              showLinkPanel: true,
              showCopyBlockButton: false,
              isExtendedManageEnabled,
              canBlotLocking,
              darkMode: {
                theme: 'light',
                bgColor: '#FFFFFF',
              },
              lightMode: {
                theme: 'light',
                bgColor: '#FFFFFF',
              },
              connectToSocket: true,
              portalId: portalSettings.id,
              socket,
              dateTimeLocale: dateTimeLocale as string,
              socketServerUrl: generalEnv.socketServerUrl,
              showToast: toast,
              iframelyHost: generalEnv.embedCodeUrl,
              onLoad: (editor) => {
                // Enable editor focus, coz it's disabled by default
                // for work in multi-editor mode
                editor.setFocus();

                setWebEditor(editor);
                editorRef.current = editor;
              },
              onSetHintIcon: onSetHintIcon,
              onGetTextFromImage: onGetTextFromImage,
              scrollingContainer: 'main-editor-scrolling-container',
              onMentionClick,
              embeddedMenuMoreAnchorOptions: {
                behavior: ['bottom', 'top', 'bottom'],
                priority: ['left', 'right', 'bottom', 'top'],
                boundariesElement: 'viewport',
              },
              canAddMembers: role.privileges.includes(OrganizationPrivilege.CAN_ADD_MEMBERS),
              /* limits handling */
              showAiLimitPopup: (usage: AiUsage) => {
                showAiLimitPopup({
                  openModal,
                  subscriptionType: orgInfo.subscriptionType,
                  billingInfo,
                  currentOrganization,
                  user,
                  usage,
                  getOpenPaywallPopup,
                  openGenAiAddonPopup,
                });
              },
              showLimitPopup: (error: AppLimitError) => {
                if (error.objectType === 'Usage') {
                  showTrafficLimitPopup({
                    openModal,
                    subscriptionType: orgInfo.subscriptionType,
                    currentOrganization,
                    getOpenPaywallPopup,
                  });
                } else if (error.objectType === 'Attachment') {
                  showAttachmentSizeLimitPopup({
                    openModal,
                    subscriptionType: orgInfo.subscriptionType,
                    currentOrganization,
                    maxAttachmentSize: orgLimits.data?.attachmentSize || 0,
                    attachmentSize: error.objectSize,
                    attachmentFileName: error.objectGlobalId,
                    currentUser: user,
                    getOpenPaywallPopup,
                  });
                } else if (error.objectType === 'Storage') {
                  showStorageSizeLimitPopup({
                    openModal,
                    subscriptionType: orgInfo.subscriptionType,
                    currentOrganization,
                    getStorageUsage,
                    attachmentSize: error.objectSize,
                    attachmentFileName: error.objectGlobalId,
                    currentUser: user,
                    isMaxSubscriptionPlan: hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons),
                    getOpenPaywallPopup,
                  });
                }
              },
              blotsLimit: getBlotsLimit(
                orgUsageSummary,
                openModal,
                orgInfo.subscriptionType,
                currentOrganization,
                getOpenPaywallPopup
              ),

              createNote: (params: EditorCreateNoteParams) => {
                return createNote({
                  workspaceId: params.workspaceId,
                  globalId: params.globalId,
                  title: params.title,
                  parentId: params.parentId,
                });
              },
              onTaskListRender: onTaskListRender,
              onKanbanRender: unleashFlags[UnleashFeatures.KANBAN_CALENDAR] ? onTaskListRender : undefined,
              onTasksFeatureComponentPropsChanged: onTasksFeatureComponentPropsChanged,
              showImageViewerForPage,
              enableImageViewer: unleashFlags[UnleashFeatures.IMAGE_VIEWER],
              maxAttachmentSize: orgLimits.data?.attachmentSize,
              getStorageUsage,
              incStorageSize,
              decStorageSize,
              emptyFeaturesPlugin: {
                enabled: true,
                useTemplate: () => window.open(`https://${generalEnv.nimbusDomain}/templates/`),
              },
              isTranscriptEnabled: true,
              isGuidegenEnabled: unleashFlags[UnleashFeatures.VIDEO_GUIDES],
              onTranscript: (params, action) => {
                if ('transcript' === action) {
                  showTranscriptPanel(params);
                } else if ('guide' === action) {
                  activateGuideGen(params);
                } else {
                  showTranscriptPanel({ ...params, aiCommand: true });
                  setActiveTabToAskAI();
                  if ('meeting_notes' === action) {
                    updateAIChat({
                      cmd: 'MEETING_NOTES',
                    });
                  } else if ('summary' === action) {
                    updateAIChat({
                      cmd: 'SUMMARY',
                    });
                  }
                }
              },
              rateEditorEnabled: unleashFlags[UnleashFeatures.RATE_THE_EDITOR] && isOwner,
              onRateEditorClick,
              onAttachmentCreated,
              onAttachmentFullScreenChange: (params) => {
                if (!params.open) {
                  setAttachmentFullscreen(null);
                } else {
                  setAttachmentFullscreen(params);
                }
              },
              drawPopupMenuInPortal: true,
            }}
          />
        </div>
        <TranscriptPanel getOpenPaywallPopup={getOpenPaywallPopup} getOpenUnlockAIPopup={getOpenUnlockAIPopup} />
        <Guidegen />
      </div>
      {transcriptPanel.opened ? (
        <TranscriptPanelHideBtn
          style={{
            top: 'calc(50% - 12px)',
            right: `calc(${transcriptPanel.visible ? 449 : 48}px - 12px)`,
          }}
          additionClass="absolute"
        />
      ) : null}
    </div>
  );
}
